import { inputRules, wrappingInputRule } from 'prosemirror-inputrules';
import { keymap } from 'prosemirror-keymap';
import type { Node as ProsemirrorNode } from 'prosemirror-model';
import { Fragment, Slice } from 'prosemirror-model';
import { liftListItem, sinkListItem, splitListItem } from 'prosemirror-schema-list';
import type { Command } from 'prosemirror-state';
import { Plugin } from 'prosemirror-state';
import { Transform } from 'prosemirror-transform';
import type { EditorView } from 'prosemirror-view';
import { BehaviorSubject, Subject } from 'rxjs';

import { WysiwygShortcuts } from '@core/wysiwyg-legacy';

import type { ListMenuController } from './list-plugin.types';
import { ListService } from './list-service-public';
import type { EditorButton, RtPMPlugin } from '../../editor/types';
import { EditorMode } from '../../editor/types';
import { DEFAULT_KEYMAP_INPUTRULE_PRIORITY, isMacOS, metaKey } from '../../helpers/utils';
import { NodeName } from '../../schema/nodes';
import { ParagraphNode } from '../basic/nodes';
import type { ChecklistEvents } from '../checklist/events';
import { BulletListNode } from './nodes/bullet-list.node';
import { OrderedListNode } from './nodes/ordered-list.node';
import type { ListSchema } from './nodes/types';

export interface ListItemKeymap {
  ['Mod-[']: Command;
  ['Mod-]']: Command;
  ['Shift-Tab']: Command;
  ['Tab']: Command;
  ['Enter']: Command;
}

const BULLET_LIST_ITEM_REGEX = /^\s*([-+*]\s)/;
const ORDERED_LIST_ITEM_REGEX = /^\s*(\d+)\.\s/;

export class ListPlugin implements RtPMPlugin {
  public api: {
    listMenuController?: ListMenuController;
    orderedListService: ListService;
    bulletListService: ListService;
  };

  public buttons = {
    toggleListMenu: <EditorButton>{
      icon: 'format-list-bulleted',
      tooltipText: 'wysiwyg.lists',
      isDropdown: true,
      isActive: (...args) => this.api.listMenuController?.isOpen(),
      run: (state, dispatch, origin) => {
        if (!this.api.listMenuController) return;
        const getState = () => this.editorView.state;

        const { backdropClick$ } = this.api.listMenuController.open(
          origin.getBoundingClientRect(),
          {
            toggleOrdered: () => {
              this.api.orderedListService.toggle(getState, this.editorView.dispatch);
              this.listMenuController?.refresh();
              this.editorView.focus();
            },
            toggleBullets: () => {
              this.api.bulletListService.toggle(getState, this.editorView.dispatch);
              this.listMenuController?.refresh();
              this.editorView.focus();
            },
            getActive: () =>
              this.api.orderedListService.isActive(this.editorView.state)
                ? NodeName.OrderedList
                : this.api.bulletListService.isActive(this.editorView.state)
                  ? NodeName.BulletList
                  : null,
          },
        );
        backdropClick$.subscribe(() => this.api.listMenuController?.close());
      },
    },
    toggleBulletList: <EditorButton>{
      icon: 'format-list-bulleted',
      tooltipText: 'wysiwyg.bulletslist',
      tooltipShortcut: WysiwygShortcuts.Bulletslist(metaKey),
      isActive: (...args) => this.api.bulletListService.isActive(...args),
      run: (state, dispatch) => {
        this.api.bulletListService.toggle(() => this.editorView.state, dispatch);
      },
    },
    toggleOrderedList: <EditorButton>{
      icon: 'format-list-numbered',
      tooltipText: 'wysiwyg.numberslist',
      tooltipShortcut: WysiwygShortcuts.Numberslist(metaKey),
      isActive: (...args) => this.api.orderedListService.isActive(...args),
      run: (state, dispatch) => {
        this.api.orderedListService.toggle(() => this.editorView.state, dispatch);
      },
    },
  };
  public eventStream$ = new Subject<ChecklistEvents>();

  private canEditCheckbox$ = new BehaviorSubject(false);
  private editorView: EditorView;
  private pasteListPlugin = new Plugin({
    props: {
      transformPasted: slice => {
        let newContent = Fragment.from();
        slice.content.forEach((node, offset, index) => {
          const lastChild = newContent.childCount
            ? newContent.child(newContent.childCount - 1)
            : null;
          const isTextOnlyParagraph =
            ParagraphNode.is(node) &&
            !!node.childCount &&
            [...Array(node.childCount)].every((_, i) => node.child(i).isText);

          if (!isTextOnlyParagraph) {
            newContent = newContent.append(Fragment.from(node));
            return;
          }

          const nodeText = node.textBetween(0, node.content.size);

          if (BULLET_LIST_ITEM_REGEX.test(nodeText)) {
            newContent = this.textNodeToBulletListItem(node, lastChild, newContent);
          } else if (ORDERED_LIST_ITEM_REGEX.test(nodeText)) {
            newContent = this.textNodeToOrderedListItem(node, lastChild, newContent);
          } else {
            newContent = newContent.append(Fragment.from(node));
          }
        });

        return new Slice(newContent, slice.openStart, slice.openEnd);
      },
    },
  });

  constructor(protected listMenuController?: ListMenuController) {}

  public beforeInit(mode: EditorMode, schema: ListSchema) {
    const { ordered_list, bullet_list, list_item, paragraph } = schema.nodes;
    this.api = {
      orderedListService: new ListService(ordered_list, list_item, paragraph),
      bulletListService: new ListService(bullet_list, list_item, paragraph),
      listMenuController: this.listMenuController,
    };

    this.canEditCheckbox$.next(EditorMode.Wysiwyg === mode);
  }

  public afterInit(mode: EditorMode, editorView: EditorView) {
    this.editorView = editorView;
  }

  public getNativeEditorPlugins(schema: ListSchema) {
    return [
      this.pasteListPlugin,
      {
        priority: DEFAULT_KEYMAP_INPUTRULE_PRIORITY,
        plugin: keymap(this.getListContainersKeyBindings(schema)),
      },
      {
        priority: DEFAULT_KEYMAP_INPUTRULE_PRIORITY,
        plugin: keymap(this.getListItemKeyBindings(schema)),
      },
      {
        priority: DEFAULT_KEYMAP_INPUTRULE_PRIORITY,
        plugin: inputRules({ rules: this.getInputRules(schema) }),
      },
    ];
  }

  private getListContainersKeyBindings(schema: ListSchema) {
    const wrapInListBulletKey = isMacOS ? 'Shift-Mod-8' : 'Shift-Ctrl-8';
    const wrapInListOrderedKey = isMacOS ? 'Shift-Mod-7' : 'Shift-Ctrl-7';
    const getState = () => this.editorView.state;

    return <Record<string, Command>>{
      [wrapInListBulletKey]: (state, dispatch) =>
        this.api.bulletListService.toggle(getState, dispatch),
      [wrapInListOrderedKey]: (state, dispatch) =>
        this.api.orderedListService.toggle(getState, dispatch),
    };
  }

  private getListItemKeyBindings(schema: ListSchema) {
    const { list_item } = schema.nodes;
    const keymap: ListItemKeymap = {
      ['Mod-[']: liftListItem(list_item),
      ['Shift-Tab']: liftListItem(list_item),
      ['Mod-]']: sinkListItem(list_item),
      ['Tab']: sinkListItem(list_item),
      ['Enter']: splitListItem(list_item),
    };
    return <Record<string, Command>>(<unknown>keymap);
  }

  private getInputRules(schema: ListSchema) {
    return [
      wrappingInputRule(/^\s*([-+*])\s$/, schema.nodes.bullet_list),
      wrappingInputRule(
        /^(1)\.\s$/,
        schema.nodes.ordered_list,
        match => ({ order: +match[1] }),
        (match, node) => node.childCount + node.attrs.order === parseInt(match[1], 10),
      ),
    ];
  }

  private textNodeToBulletListItem(
    textNode: ProsemirrorNode,
    lastChild: ProsemirrorNode | null,
    newContent: Fragment,
  ): Fragment {
    const { schema } = this.editorView.state;
    const nodeText = textNode.textBetween(0, textNode.content.size);
    const [allMatch] = nodeText.match(BULLET_LIST_ITEM_REGEX)!;
    const listItem = schema.nodes.list_item.create(
      null,
      textNode.replace(0, allMatch.length, Slice.empty),
    );

    if (lastChild && BulletListNode.is(lastChild)) {
      const tr = new Transform(lastChild);
      tr.insert(lastChild.content.size, listItem);
      return newContent.replaceChild(newContent.childCount - 1, tr.doc);
    } else {
      const list = schema.nodes.bullet_list.createChecked(null, [listItem]);
      return newContent.append(Fragment.from(list));
    }
  }

  private textNodeToOrderedListItem(
    textNode: ProsemirrorNode,
    lastChild: ProsemirrorNode | null,
    newContent: Fragment,
  ): Fragment {
    const { schema } = this.editorView.state;
    const nodeText = textNode.textBetween(0, textNode.content.size);
    const [allMatch, orderStr] = nodeText.match(ORDERED_LIST_ITEM_REGEX)!;
    const order = parseInt(orderStr, 10);
    const listItem = schema.nodes.list_item.create(
      null,
      textNode.replace(0, allMatch.length, Slice.empty),
    );

    if (order === 1) {
      const list = schema.nodes.ordered_list.createChecked(null, [listItem]);
      return newContent.append(Fragment.from(list));
    } else if (lastChild && OrderedListNode.is(lastChild)) {
      if (lastChild.childCount + 1 !== order) {
        return newContent.append(Fragment.from(textNode));
      }
      const tr = new Transform(lastChild);
      tr.insert(lastChild.content.size, listItem);
      return newContent.replaceChild(newContent.childCount - 1, tr.doc);
    }

    return newContent.append(Fragment.from(textNode));
  }
}
